import React, { useMemo, useState } from 'react'
import * as yup from 'yup'
import { FastField, FieldProps, Formik } from 'formik'
import { isEmpty, mergeRight } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from 'react-use'
import { Divider, FormField, FormLoader, TextField, TextInput } from 'components'
import { TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import { formUtils } from 'utils'
import { jsonStringSearch } from 'utils/string'
import { agencyApiSelectors } from 'modules/api/agency'
import { apiStateSelectors } from 'modules/api/apiState'
import { userApiEvents, userApiSelectors } from 'modules/api/user'
import { portfolioApiEvents, portfolioApiSelectors } from 'modules/api/portfolio'
import { bookSegmentsEvents } from '../../state/state'
import { getDefaultSegmentId } from '../../state/selectors'
import { TSegmentFormValues } from '../../types'
import SegmentFormSearch, { TResultSelectData } from './SegmentFormSearch'
import SegmentFormPortfoliosTable from './SegmentFormPortfoliosTable'
import SegmentFormTeamTable from './SegmentFormTeamTable'
import {
  transformPortfolioFormTable,
  transformPortfolioSummaryForSearch,
  transformTeamMemberForSearch,
  transformTeamMemberForTable,
} from './utils'
import styles from './SegmentForm.module.scss'

const validationSchema = yup.object().shape({
  name: yup.string().required('Required'),
})

const NO_GENERAL_ERRORS: string[] = []
const NO_FIELD_ERRORS: Record<string, string> = {}

const SegmentForm = ({ id = '' }: { id: string }): React.ReactElement => {
  const dispatch = useDispatch()
  const isNew = id === 'new'

  useEffectOnce(() => {
    dispatch(userApiEvents.team_request())
    dispatch(portfolioApiEvents.portfolioSummaries_request())
  })

  const defaultSegmentId = useSelector(getDefaultSegmentId)
  const isDefault = id === defaultSegmentId
  const segment = useSelector(state => agencyApiSelectors.getSegmentById(state)(id))
  const teamMembers = useSelector(userApiSelectors.getTeam)
  const portfolios = useSelector(portfolioApiSelectors.getPortfolioSummaries)

  const [portfolioQuery, setPortfolioQuery] = useState('')
  const [teamQuery, setTeamQuery] = useState('')

  const initialValues = useMemo<TSegmentFormValues>(
    () => ({
      id,
      name: segment?.name ?? '',
      portfolios: portfolios.filter(summary => (summary.segments ?? []).includes(id)).map(transformPortfolioFormTable),
      team: teamMembers.filter(member => (member.segments ?? []).includes(id)).map(transformTeamMemberForTable),
      deletedPortfolios: [],
      deletedTeamMemberships: [],
    }),
    [id, segment, portfolios, teamMembers],
  )

  // Only saved leases and users need a request to undo; anything added during this edit can just be dropped
  const savedPortfolioIds = useMemo(() => initialValues.portfolios.map(p => p.id), [initialValues])
  const savedTeamIds = useMemo(() => initialValues.team.map(m => m.id), [initialValues])

  const saveEvent = isNew ? bookSegmentsEvents.createSegment_request : bookSegmentsEvents.updateSegment_request
  const isSubmitting = useSelector(state => apiStateSelectors.isLoading(state)([saveEvent]))

  /**
   * apiState never clears an error, so look errors up by the save dispatched from
   * this form rather than by event: otherwise one failed save would put every later
   * visit to the form into its error state.
   */
  const [submitted, setSubmitted] = useState<ReturnType<typeof saveEvent> | null>(null)
  const generalErrors = useSelector(state =>
    submitted ? apiStateSelectors.getGeneralFormErrorsByEvent(state)(submitted) : NO_GENERAL_ERRORS,
  )
  const fieldErrors = useSelector(state =>
    submitted ? apiStateSelectors.getFormFieldErrorsByEvent(state)(submitted) : NO_FIELD_ERRORS,
  )
  const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

  const handleSubmit = (values: TSegmentFormValues) => {
    const event = saveEvent(values)
    setSubmitted(event)
    dispatch(event)
  }

  return (
    <div className={styles.root}>
      <Formik
        validateOnBlur
        validateOnChange
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleChange, handleBlur, handleSubmit, values, touched, submitCount, errors, setFieldValue }) => {
          const getFieldError = formUtils.getFieldError(submitCount, touched, mergeRight(errors, fieldErrors))

          const formLoaderState = isSubmitting
            ? 'submitting'
            : (submitCount > 0 && !isEmpty(errors)) || hasApiErrors
            ? 'error'
            : undefined

          const addedPortfolioIds = values.portfolios.map(p => p.id)
          const addedTeamIds = values.team.map(m => m.id)

          const portfolioResultSets = [
            portfolios
              .filter(summary => !addedPortfolioIds.includes(summary.portfolioId))
              // a lease can only be in one segment besides the default, unless it was removed from this one during this edit
              .filter(
                summary =>
                  (summary.segments ?? []).every(segmentId => segmentId === defaultSegmentId) ||
                  values.deletedPortfolios.includes(summary.portfolioId),
              )
              .filter(summary => !summary.terminatedReason)
              .filter(summary => jsonStringSearch(portfolioQuery, summary))
              .map(transformPortfolioSummaryForSearch),
          ]

          const teamResultSets = [
            teamMembers
              .filter(member => !addedTeamIds.includes(member.userId))
              .map(transformTeamMemberForSearch)
              .filter(result => jsonStringSearch(teamQuery, result)),
          ]

          const handlePortfolioAdded = (e: React.SyntheticEvent, { result }: TResultSelectData) => {
            const portfolio = portfolios.find(p => p.portfolioId === result.id)
            if (portfolio) {
              setFieldValue('portfolios', [...values.portfolios, transformPortfolioFormTable(portfolio)])
              setFieldValue(
                'deletedPortfolios',
                values.deletedPortfolios.filter(deletedId => deletedId !== portfolio.portfolioId),
              )
            }
          }

          const handleTeamMemberAdded = (e: React.SyntheticEvent, { result }: TResultSelectData) => {
            const teamMember = teamMembers.find(member => member.userId === result.id)
            if (teamMember) {
              setFieldValue('team', [...values.team, transformTeamMemberForTable(teamMember)])
              setFieldValue(
                'deletedTeamMemberships',
                values.deletedTeamMemberships.filter(deletedId => deletedId !== teamMember.userId),
              )
            }
          }

          const handleRemovePortfolio = (portfolioId: string): void => {
            setFieldValue(
              'portfolios',
              values.portfolios.filter(p => p.id !== portfolioId),
            )
            if (savedPortfolioIds.includes(portfolioId)) {
              setFieldValue('deletedPortfolios', [...values.deletedPortfolios, portfolioId])
            }
          }

          const handleRemoveTeamMember = (userId: string): void => {
            setFieldValue(
              'team',
              values.team.filter(m => m.id !== userId),
            )
            if (savedTeamIds.includes(userId)) {
              setFieldValue('deletedTeamMemberships', [...values.deletedTeamMemberships, userId])
            }
          }

          return (
            <FormLoader
              onSubmit={handleSubmit}
              state={formLoaderState}
              submittingText={isNew ? 'Creating segment' : 'Updating segment'}
              buttonProps={{ children: isNew ? 'Create segment' : 'Save segment' }}
            >
              <FormField>
                <FastField
                  name="name"
                  render={({ field }: FieldProps<string, TSegmentFormValues>) => (
                    <TextField
                      {...field}
                      inputComponent={
                        <TextInput
                          type={TextFieldTypes.text}
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          placeholder="e.g. Alpha Portfolio, Late Payers"
                          onBlur={handleBlur}
                        />
                      }
                      label="Segment Name"
                      disabled={isDefault}
                      error={getFieldError('name')}
                    />
                  )}
                />
              </FormField>

              <Divider />

              {!isDefault && (
                <>
                  <SegmentFormSearch
                    name="search-leases"
                    placeholder="Search leases..."
                    heading="Leases"
                    onResultSelect={handlePortfolioAdded}
                    onSearchChange={({ target }) => setPortfolioQuery(target.value)}
                    resultSets={portfolioResultSets}
                  />

                  {values.portfolios.length > 0 ? (
                    <SegmentFormPortfoliosTable data={values.portfolios} onRemove={handleRemovePortfolio} />
                  ) : (
                    <p>No leases yet. Click the &#43; to add a lease to this segment.</p>
                  )}
                  <Divider />
                </>
              )}

              <SegmentFormSearch
                name="search-team"
                placeholder="Search users..."
                heading="Users"
                onResultSelect={handleTeamMemberAdded}
                onSearchChange={({ target }) => setTeamQuery(target.value)}
                resultSets={teamResultSets}
              />

              {values.team.length > 0 ? (
                <SegmentFormTeamTable data={values.team} onRemove={handleRemoveTeamMember} />
              ) : (
                <p>No users yet. Click the &#43; to add a user to this segment.</p>
              )}

              <Divider />
            </FormLoader>
          )
        }}
      </Formik>
    </div>
  )
}

export default SegmentForm
